import type { ParsedUrlQuery } from "querystring";

import axios from "axios";
import type { GetServerSidePropsContext, PreviewData } from "next";

import * as CONSTANTS from "@/constants/common";
import { getCookieSSR } from "@/utils/auth";

const isRefreshing = false;
const failedQueue: any[] = [];
let context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>;

// getServerSideProps
export const setServerSidePropsContext = (
  _context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>
) => {
  context = _context;
};

const _APIInstance = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_BASE_URL_SERVER}`,
});

_APIInstance.interceptors.request.use((config: any) => {
  const token = getCookieSSR(
    CONSTANTS.ACCESS_TOKEN,
    typeof window === "undefined"
      ? { req: context?.req, res: context?.res }
      : {}
  );
  const lang = getCookieSSR(
    CONSTANTS.LANG,
    typeof window === "undefined"
      ? { req: context?.req, res: context?.res }
      : {}
  );

  const currency = getCookieSSR(
    "currency",
    typeof window === "undefined"
      ? { req: context?.req, res: context?.res }
      : {}
  );
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (lang && !config.headers["lang"]) {
    config.headers["lang"] = lang;
  }
  if (lang && !config.headers["Accept-Language"]) {
    config.headers["Accept-Language"] = lang;
  }
  if (currency) {
    config.headers["currency"] = currency;
  }
  return config;
});

export const setToken = (token?: string) => {
  if (token) {
    APIInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete APIInstance.defaults.headers.common.Authorization;
  }
};

_APIInstance.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (error: any) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;

            return APIInstance(originalRequest);
          })
          .catch(() => {
            return null;
          });
      }

      originalRequest._retry = true;
    }

    return Promise.reject(error?.response?.data?.error || error);
  }
);

export default _APIInstance;

export const APIInstance = _APIInstance;
